'use client';

import dynamic from 'next/dynamic';
import { Suspense, useEffect, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { sdk } from '@farcaster/miniapp-sdk';
import { GlobalActivityTicker } from '@/components/layout/activity-ticker';
import { AppBottomNav } from '@/components/layout/AppBottomNav';
import { LobbyFooter } from '@/features/lobby/panels/LobbyFooter';
import { MobileChipRailHost } from '@/features/lobby/panels/MobilePanelChipRail';
import { MobileChromeErrorBoundary } from '@/components/layout/mobile-chrome-error-boundary';
import { PreviewModeBanner } from '@/components/layout/preview-mode-banner';
import { PwaInstallPrompt } from '@/components/layout/pwa-install-prompt';
import { MobileNavProvider } from '@/providers/mobile-nav-provider';
import { useAppModeConfig } from '@/lib/use-app-mode';
import { cn } from '@/lib/utils';

/**
 * Combined mobile bottom chrome height (LobbyFooter `h-9` 36px +
 * AppBottomNav `h-14` 56px). Exported for any callers that still need
 * the literal — the layout itself no longer depends on the constant
 * because the chrome stacks are in-flow flex children of the shell
 * and self-size via the flex algorithm (see `MobileChromeTop` /
 * `MobileChromeBottom` below).
 */
export const MOBILE_BOTTOM_CHROME_PX = 92;

/**
 * `MobileBoardRail` is dynamic-imported with SSR disabled because it
 * consumes `useParams` / `useSearchParams` / `useWallet` hooks that
 * read runtime navigation + wagmi state. Loading it client-only keeps
 * the shared AppShell lean on the server and avoids hydration spikes
 * for routes (like `/chat` and `/create`) that would otherwise never have
 * pulled that bundle in.
 */
const MobileBoardRail = dynamic(
  () =>
    import('@/features/lobby/panels/MobileBoardRail').then(
      (m) => m.MobileBoardRail,
    ),
  { ssr: false, loading: () => null },
);

/**
 * Routes that own their full chrome. The V1 cleanup retired the legacy
 * `Header` / `Footer` / desktop `GlobalActivityTicker` stack entirely —
 * every page now either paints its own header strip (lobby, admin
 * console, chat sheet, poke utility) or is a self-contained surface
 * (tutorial walkthrough, launch wizard). The list below is therefore
 * effectively "every public route in the app"; we keep the explicit
 * arrays for readability and to make it obvious which pages opt into
 * the shared mobile chrome (top board rail + bottom AppBottomNav).
 *
 * `/poke` is an orphan harvester utility styled in the lobby HUD
 * aesthetic. `/stats`, `/monitoring`, `/replay`, and
 * `/demo/sdk-embed` are admin / partner-facing diagnostics that ship
 * with their own self-contained layouts (they used to ride on the
 * classic `Header` but were never re-skinned to the V2 lobby look).
 * Until they migrate to the AdminShell, listing them here at least
 * keeps them from crashing now that the desktop `Header` / `Footer`
 * components are gone.
 */
const CHROMELESS_ROUTES = [
  '/',
  '/lobby',
  '/create',
  '/tutorial',
  '/builders',
  '/poke',
  '/stats',
  '/monitoring',
  '/replay',
  '/demo/sdk-embed',
  '/demo/objects',
];

/** Route prefixes (no leading slash) — matches `/board` (+ `/board/...`)
 *  and the cinematic admin console at `/admin` (+ `/admin/unlock`,
 *  `/admin/contracts`, …). The admin pages all wrap themselves in
 *  `AdminShell`, which paints its own full-viewport amber radial
 *  backdrop and header strip.
 *
 *  `/builders` — the public builder portal (API keys, usage, webhooks,
 *  docs, billing). It owns its own SaaS-style sidebar shell
 *  (`BuilderShell`, see `src/app/builders/layout.tsx`) so the lobby
 *  bottom nav / chip rail / activity ticker would only get in the way.
 *  Listing the prefix here keeps every nested route (`/builders/keys`,
 *  `/builders/usage`, …) under that shell. */
const CHROMELESS_PREFIXES: string[] = ['board', 'admin', 'builders'];

function matchesChromelessPrefix(pathname: string, prefix: string): boolean {
  const base = `/${prefix}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Routes that mount the shared mobile chrome hoist:
 *   • GlobalActivityTicker (top)
 *   • MobileBoardRail ("My boards")
 *   • AppBottomNav (Lobby / + Board / Chat / Profile)
 *   • trimmed LobbyFooter (Terms · X · MC)
 *
 * Every `/board/...` route, `/chat`, `/create`, `/lobby`, and `/poke`
 * all share the same "current board" context driven by the rail — so
 * the rail, the global ticker, and the bottom nav should render
 * identically across them. `/create` was added after the original
 * lobby redesign because the launch wizard was the only remaining
 * route where the "+" bottom-nav entry landed the user on a chromeless
 * page — breaking the invariant that the mobile chrome is "always on".
 *
 * `/lobby` is now where the full `LobbyClient` lives (the marketing
 * landing took over `/`). It sits inside the `(lobby)` route group as
 * `(lobby)/lobby/page.tsx` so the layout-shared canvas / panel state
 * persists across navigations to and from `/board/[boardId]/*`.
 * Matching `/lobby` here is what gives the lobby the same top-rail /
 * bottom-nav chrome every other primary route already has.
 *
 * (Profile / Portfolio surfaces are no longer pages — they open as
 * `ProfileModal` from the bottom nav, so they don't need a chrome
 * route here.)
 *
 * `/tutorial` opts in for two reasons:
 *   1. **Layout** — the tutorial mounts the same `LobbyClient` tree
 *      as `/`, which is wrapped by `LaunchTransitionProvider` in two
 *      `h-full w-full` divs. Those collapse to 0 unless the shell
 *      gives `<main>` a definite height, which only happens on the
 *      `h-[100dvh] overflow-hidden` shell variant below. Without
 *      this match the entire board canvas rendered as a black void
 *      between the top owned-tiles strip and the bottom dialogue
 *      panel on every mobile tutorial visit.
 *   2. **Chip rail** — `LobbyClient`'s tutorial bridge auto-selects
 *      mobile panels (`yield-pill` / `rewards-pill` / `chat-pill`) on
 *      the matching scripted steps. The chip rail is only mounted
 *      via `MobileChromeTop` → `MobileChipRailHost`, so without it
 *      the panel-spotlight beats had nothing on screen to highlight.
 */
function matchesMobileChromeRoute(pathname: string): boolean {
  if (
    pathname === '/chat' ||
    pathname === '/create' ||
    pathname === '/lobby' ||
    pathname === '/poke' ||
    pathname === '/tutorial'
  ) {
    return true;
  }
  return matchesChromelessPrefix(pathname, 'board');
}

/**
 * Shared mobile chrome top (`lg:hidden`): global activity ticker +
 * owned-board rail + panel chip-rail. Rendered as an **in-flow**
 * `flex-shrink-0` sibling of `<main>` inside the shell's flex column
 * — *not* `position: fixed` — so the layout below it is
 * self-correcting from the SSR HTML, with no JS measurement, no CSS
 * variable, and no race against the dynamic-imported
 * `MobileBoardRail` chunk.
 *
 * Why this matters: a `position: fixed` chrome combined with a
 * `padding-top: var(--mobile-chrome-top, …)` on `<main>` was the
 * previous design. The CSS variable was set by a `useLayoutEffect` +
 * `ResizeObserver` on the chrome wrapper — but that effect only runs
 * after JS hydration. The server-rendered HTML lands with the
 * variable unset, so the fallback (`0px`) wins for the entire
 * pre-hydration window. On cellular / in-app webviews that window is
 * hundreds of milliseconds: long enough that users saw the first
 * frame of the lobby with content sitting **behind** the fixed
 * chrome (panel pills hidden under the joined-boards rail, lobby
 * search/sort cropped, MobileBoardRail chunk landing late and
 * shoving everything down with a visible jump). Bouncing through
 * `/tutorial` papered over it because the chunk was warm on
 * return, so the variable was set on the first commit.
 *
 * Making the chrome a real flex sibling collapses the entire
 * problem: the chrome consumes its measured height from the flex
 * column; `<main>` (`flex-1 min-h-0`) gets exactly the leftover
 * band. When `MobileBoardRail` mounts a frame later the chrome's
 * intrinsic height grows, the flex algorithm shrinks main to match,
 * and content reflows down — never under — the chrome. There is no
 * "broken first frame" because there is no JS-measured value in the
 * critical layout path.
 *
 * Mobile-chrome routes set the shell to `h-[100dvh] overflow-hidden`,
 * so there is no document scroll either way; the only behavioural
 * difference between `fixed` and in-flow is the bug we just fixed.
 *
 * The chip-rail is rendered through `MobileChipRailHost`, which reads
 * its config from `MobileChromeContext` — the active route consumer
 * (`LobbyClient`, `/chat`) registers the rail's state via the
 * `useChipRail` hook so we don't have to thread props through the
 * shell. When no consumer is registered (e.g. `/create`, lobby mode)
 * the host renders `null` and the top chrome is just the ticker +
 * board rail.
 */
function MobileChromeTop() {
  // `MobileChromeErrorBoundary` scopes any synchronous render-time
  // throw inside the ticker / rail / chip-rail to *just this strip*.
  // Before this boundary, a malformed bigint in a portfolio seat
  // (driving `useLobbySeatHoldings` → `MobileBoardRail`) would
  // escape every boundary — `(lobby)/error.tsx` is a sibling under
  // `<main>`, not a parent here — and surface as Next.js's bare
  // "Application error: a client-side exception has occurred"
  // production fallback. The boundary fails-soft to `null`; the
  // flex column shrinks the freed band, `<main>` keeps painting,
  // and the rest of the app stays usable.
  return (
    <MobileChromeErrorBoundary label="top">
      <div className="lg:hidden flex-shrink-0 relative z-30">
        <GlobalActivityTicker />
        <MobileBoardRail />
        <MobileChipRailHost />
      </div>
    </MobileChromeErrorBoundary>
  );
}

/**
 * Shared mobile chrome bottom (`lg:hidden`): the trimmed LobbyFooter
 * stacked directly above the four-tab AppBottomNav. Like
 * `MobileChromeTop`, rendered as an in-flow `flex-shrink-0` sibling
 * of `<main>` rather than `position: fixed`, so route content
 * (`<main flex-1 min-h-0>`) automatically gets exactly the middle
 * band of the shell with no JS-measured padding. See
 * `MobileChromeTop` for the full rationale on why `fixed` chrome
 * was retired.
 *
 * Height budget (informational — the flex algorithm consumes the
 * intrinsic height directly):
 *   • LobbyFooter (`h-9`)     36px
 *   • AppBottomNav (`h-14`)   56px
 *   • total                   92px (`MOBILE_BOTTOM_CHROME_PX`)
 */
function MobileChromeBottom() {
  // `AppBottomNav` calls `useSearchParams()` to mirror lobby query state
  // into the +/Profile tab links. The hook forces a CSR bailout on any
  // route that prerenders, so wrap it in a Suspense fence here (and in
  // the non-chrome-route bottom nav below) — the page tree above us
  // can still statically prerender, the bottom nav simply hydrates
  // client-side. Fallback is `null` so the chrome row reserves
  // height intrinsically (LobbyFooter renders synchronously) and
  // hydration just fills in the nav's interactive bits.
  //
  // Same boundary rationale as `MobileChromeTop`: `AppBottomNav`'s
  // `useLobbyHeaderStats` consumes the same wallet-scoped portfolio
  // payload and would otherwise escape every boundary on a bad row.
  return (
    <MobileChromeErrorBoundary label="bottom">
      <div className="lg:hidden flex-shrink-0 flex flex-col relative z-40">
        <LobbyFooter />
        <Suspense fallback={null}>
          <AppBottomNav />
        </Suspense>
      </div>
    </MobileChromeErrorBoundary>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const modeConfig = useAppModeConfig();
  const isChromelessPrefix = CHROMELESS_PREFIXES.some((prefix) =>
    matchesChromelessPrefix(pathname, prefix),
  );
  const isChromelessExact = CHROMELESS_ROUTES.includes(pathname);
  const isChromeless = isChromelessExact || isChromelessPrefix;
  const hasMobileChrome = matchesMobileChromeRoute(pathname);
  // `/debug` is the rescue surface — see `src/app/debug/page.tsx`
  // for the full rationale. It has to paint even when a wallet
  // extension blew up pre-hydration or a cookie corruption would
  // crash Privy on mount, so we bypass not just chrome but every
  // wrapper AppShell normally mounts:
  //   - `MobileNavProvider` (depends on React context, harmless but
  //     not needed on `/debug`)
  //   - `GlobalActivityTicker` + `MobileBoardRail` (hit TanStack
  //     Query, which is gated behind `<Providers>` — already
  //     bypassed for `/debug` upstream, so a naive mount here
  //     would crash with "No QueryClient set")
  //   - `AppBottomNav` (same; reads wallet state via Privy)
  //   - `PreviewModeBanner` + `PwaInstallPrompt` (both read
  //     `navigator.*` and can throw in locked-down webviews)
  // Returning `children` verbatim matches the "pass-through"
  // pattern used by the route-level layout (`app/debug/layout.tsx`)
  // and keeps the rescue page a true zero-dependency surface.
  if (pathname === '/debug') {
    return <>{children}</>;
  }

  useEffect(() => {
    // `sdk.actions.ready()` is a no-op outside a Farcaster mini-app
    // host, but the SDK doesn't audit `window.parent` / `postMessage`
    // shape itself — on certain in-app webviews (older MetaMask
    // builds, Coinbase Wallet on iOS) the underlying postMessage
    // bridge throws synchronously when the host frame doesn't
    // recognise the message channel. Swallow the throw so a missing
    // bridge can't take down the entire shell mount.
    try {
      void sdk.actions.ready();
    } catch (err) {
      console.warn('[app-shell] miniapp-sdk ready() failed', err);
    }
  }, []);

  // Register the service worker on app boot so:
  //   1. Chromium browsers consider us installable (without this
  //      the `beforeinstallprompt` event never fires).
  //   2. The minimal fetch handler in /sw.js is active for offline
  //      navigation fallback.
  // Defers to the push-notification flow's own registration when
  // the user opts into notifications — both `register('/sw.js')`
  // calls resolve to the same registration so the double-call is
  // idempotent. Guarded for iOS WKWebView which doesn't support SW
  // in third-party browsers.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;

    const host = location.hostname;
    const isLocalDev =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '[::1]';

    // Never register on local dev: HMR + Next chunk URLs change between
    // restarts; a controlling SW (even with a passthrough for `/_next/`)
    // has caused ChunkLoadError for `app/layout.js` when an old SW or its
    // cache interfered. Tear down any existing registration from prior
    // sessions so localhost always loads chunks straight from the dev server.
    if (isLocalDev) {
      try {
        void navigator.serviceWorker.getRegistrations().then((regs) => {
          for (const r of regs) {
            try {
              void r.unregister();
            } catch {
              // best-effort cleanup
            }
          }
        });
      } catch {
        // Some in-app webviews expose `serviceWorker` but throw
        // synchronously on `getRegistrations` — swallow.
      }
      return;
    }

    // Production / preview: HTTPS or secure contexts only (insecure HTTP
    // origins skip SW — no meaningful push/PWA support there anyway).
    const insecure = location.protocol !== 'https:' && !isLocalDev;
    if (insecure) return;

    try {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Registration can fail in private browsing or with strict
        // CSP — swallow quietly, the app still works without a SW.
      });
    } catch {
      // In-app webviews (MetaMask, Coinbase, Trust, Farcaster) often
      // expose the `serviceWorker` API but reject `register()`
      // synchronously when the parent app blocks it. Swallow so a
      // missing SW can't propagate as a render-time crash.
    }
  }, []);

  // Every route is now chromeless on desktop — the legacy V1 `Header`
  // (`takeover.fun` brand + Leaderboard + Boardroom-classic links) and
  // marketing `Footer` were retired with the `/coin/...` page tree. The
  // `modeConfig.isLandingOnly` branch is preserved so a future pre-launch
  // mode can opt into the same shell without re-introducing chrome.
  void modeConfig;
  void isChromeless;
  return (
    <MobileNavProvider>
      <div
        // Mobile-chrome routes lock the shell to a single `100dvh`
        // slab with no document scroll: `MobileChromeTop` and
        // `MobileChromeBottom` are in-flow flex children, so
        // `<main>` (`flex-1 min-h-0`) automatically receives the
        // middle band with no JS-measured padding. Internal scroll
        // (panel overlays, chat list, portfolio scroller) is the
        // only scroll surface inside the band.
        //
        // Desktop keeps `min-h-screen` so long pages (admin, stats)
        // scroll naturally — the chrome stacks are `lg:hidden` and
        // collapse out of the flex column entirely on those
        // breakpoints.
        className={cn(
          'bg-background text-foreground flex flex-col',
          hasMobileChrome
            ? 'h-[100dvh] overflow-hidden lg:h-auto lg:min-h-screen lg:overflow-visible'
            : 'min-h-screen',
        )}
      >
        {hasMobileChrome && <MobileChromeTop />}
        <main
          className={cn(
            'flex-1 min-h-0',
            // Mobile-chrome routes scope `<main>` to its share of
            // the shell flex column and clip any panel-internal
            // overflow (the panel overlays / chat list manage
            // their own scroll). Desktop drops the clip so HUD
            // glow effects on the lobby canvas don't get cut off
            // at main's edges.
            hasMobileChrome && 'overflow-hidden lg:overflow-visible',
          )}
        >
          {children}
        </main>
        {hasMobileChrome ? (
          <MobileChromeBottom />
        ) : (
          // Routes without the shared rail (`/admin`, `/stats`, `/poke`
          // when accessed as a deep-link without a board context, etc.)
          // still get the bottom nav so the user can hop back into the
          // lobby / chat / profile from any phone screen. These routes
          // don't lock the shell to `100dvh`, so the nav stays
          // fixed-positioned for them — the shell scrolls and the
          // nav has to stay anchored. Same Suspense fence as
          // `MobileChromeBottom` — `useSearchParams()` inside
          // `AppBottomNav` would otherwise CSR-bail every
          // prerendered admin/dev route. Same `MobileChromeErrorBoundary`
          // wrap so an `AppBottomNav` crash on these routes doesn't
          // escape to `global-error.tsx` and replace the whole admin
          // surface — the nav fails-soft to nothing instead.
          <MobileChromeErrorBoundary label="bottom">
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40">
              <Suspense fallback={null}>
                <AppBottomNav />
              </Suspense>
            </div>
          </MobileChromeErrorBoundary>
        )}
        <PreviewModeBanner />
        <PwaInstallPrompt />
      </div>
    </MobileNavProvider>
  );
}
